import { Button, useTheme } from "@mui/material";
import { LayoutGroup, motion } from "motion/react";

type AnimatedTabs = {
    activeTab: string;
    setActiveTab: (newValue: string) => void;
    tabs: Array<{ id: string; label: string }>;
};

const MotionDiv = motion.div;

/**
 * Component to render a tabbed interface for switching between tabs.
 *
 * Props:
 * - `activeTab` (string) - The currently selected tab. Determines which tab is highlighted.
 * - `setActiveTab` (function)- A function to change the currently active tab. Invoked when a tab is clicked.
 * - `tabs` (Array<{ id: string; label: string }>) - A list of tab objects containing a unique `id` and a `label` for each tab.
 *
 * @returns {JSX.Element}  The rendered tabbed interface component.
 * @example
 * <AnimatedTabs
 *    activeTab={activeTab}
 *    setActiveTab={setActiveTab}
 *    tabs={tabs}
 * />
 */
const AnimatedTabs = ({ activeTab, setActiveTab, tabs }: AnimatedTabs) => {
    const theme = useTheme();

    return (
        <LayoutGroup>
            <div
                style={{
                    position: "relative",
                    display: "flex",
                    gap: "5px",
                    padding: "0.5rem",
                    background: theme.palette.animatedTabBackground,
                    borderRadius: "6px",
                    width: "fit-content"
                }}
            >
                {tabs.map(tab => {
                    const isActive = activeTab === tab.id;

                    return (
                        <div key={tab.id} style={{ position: "relative" }}>
                            {isActive && (
                                <MotionDiv
                                    layoutId="tabBackground"
                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        borderRadius: "6px",
                                        backgroundColor: theme.palette.animatedTabActiveBackground,
                                        zIndex: 0
                                    }}
                                />
                            )}
                            <Button
                                onClick={() => setActiveTab(tab.id)}
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    textTransform: "none",
                                    padding: "4px 8px",
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    borderRadius: "6px",
                                    minWidth: "64px",
                                    color: isActive ? theme.palette.animatedTabActiveText : theme.palette.animatedTabText,
                                    transition: "color 200ms ease-in-out",
                                    "&:hover": {
                                        backgroundColor: theme.palette.animatedTabHoverBackground,
                                        color: isActive ? theme.palette.animatedTabActiveText : theme.palette.animatedTabText
                                    }
                                }}
                            >
                                {tab.label}
                            </Button>
                        </div>
                    );
                })}
            </div>
        </LayoutGroup>
    );
};

export default AnimatedTabs;
